// Pure report-block builder for the Report modal.
// No React, no DOM, no setters. Both the PDF and DOCX export handlers
// call buildReportBlocks with the same params object.

import { fmtMDY, fmtMDYTime } from "./format.js";
import { srcLabel, citationChips, dedupeCitationsByQuote } from "../citations.js";
import { sideChanceText } from "../forecast/formatPct.js";
import { bucketLabel, bucketPctText, formatBucketLabel, selectLead } from "../forecast/multiway.js";
import { selectEvidenceColumns } from "./evidenceColumns.js";
import { strengthColor } from "./evidenceStrength.js";

// Font name pinned here so both the PDF (inline style) and DOCX
// (TextRun.font) reference the same string without coordination.
export const REPORT_FONT = "Roboto Condensed";

// Strip a leading sentence that merely RESTATES the forecast probability, e.g.
// "The probability of X … is extremely low at 4%." — so the summary opens on the
// actual analysis instead of re-stating the headline number the reader already sees.
// Conservative: only fires when the text OPENS with a probability-recap phrasing
// ("The probability/likelihood/chance/odds …" or "There is a …") AND that opening
// sentence contains a percentage figure. Anything else is returned untouched, and it
// never blanks out the whole summary (if the recap is the only sentence, keep it).
// Shared by the modal render and both exports so the in-app + PDF/DOCX text agree.
export function stripProbabilityRecap(text) {
  if (!text || typeof text !== "string") return text;
  const t = text.replace(/^\s+/, "");
  // Only act when the summary OPENS on a probability-recap phrasing.
  const opener = /^(?:the\s+(?:probability|likelihood|chance|odds)\b|there(?:\s+is|'s)\s+a\b)/i;
  if (!opener.test(t)) return text;
  // First sentence: up to the first .!? that ENDS a sentence (followed by
  // whitespace or end-of-string) — the lookahead skips a decimal point ("12.5%")
  // whose "." is followed by a digit, so the split lands on real sentence breaks.
  const first = t.match(/^[\s\S]*?[.!?]+(?=\s|$)/);
  if (!first) return text;
  const sentence = first[0];
  // Only strip if that opening sentence actually restates a probability figure,
  // and guard against a runaway match swallowing real analysis.
  if (sentence.length > 400 || !/\d+(?:\.\d+)?\s*%/.test(sentence)) return text;
  const rest = t.slice(sentence.length).replace(/^\s+/, "");
  // Never blank out the whole summary (recap was the only sentence) → keep it.
  return rest.length > 0 ? rest : text;
}

// Strip a leading sentence that merely RESTATES the question, e.g.
// "The question asks whether September 2027 will see a full normalization of …" —
// so the summary opens on the actual assessment instead of echoing the prompt the
// reader is already looking at. Conservative, mirroring stripProbabilityRecap: only
// fires when the text OPENS with a question-restatement phrasing ("The/This question
// asks/is/concerns/considers …"), strips just that first sentence, guards against a
// runaway match swallowing real analysis, and never blanks out the whole summary (if
// the restatement is the only sentence, keep it). Shared by the modal render and both
// exports so the in-app + PDF/DOCX text agree.
export function stripQuestionRestatement(text) {
  if (!text || typeof text !== "string") return text;
  const t = text.replace(/^\s+/, "");
  // Only act when the summary OPENS on a question-restatement phrasing.
  const opener = /^(?:this|the)\s+question\b/i;
  if (!opener.test(t)) return text;
  // First sentence: up to the first .!? that ENDS a sentence (followed by whitespace
  // or end-of-string). The lookahead skips a decimal point ("$1.5") whose "." is
  // followed by a digit, so the split lands on real sentence breaks.
  const first = t.match(/^[\s\S]*?[.!?]+(?=\s|$)/);
  if (!first) return text;
  const sentence = first[0];
  // Guard against a runaway match swallowing the entire summary.
  if (sentence.length > 400) return text;
  const rest = t.slice(sentence.length).replace(/^\s+/, "");
  // Never blank out the whole summary (restatement was the only sentence) → keep it.
  return rest.length > 0 ? rest : text;
}

// The forecast summary as it should read to a user: with the leading question
// restatement and/or probability recap peeled off (each conservatively). A leading
// restatement is removed first, then a probability recap that may follow it. The
// single sanitizer every summary display shares (modal, PDF/DOCX export, and the
// in-chat summary post) so the text agrees everywhere.
export function cleanForecastSummary(text) {
  return stripProbabilityRecap(stripQuestionRestatement(text));
}

// Color map shared by both export renderers. Hex strings without the leading #
// (docx TextRun.color and html2canvas both accept this form).
export const RC = {
  purple: "9966ff",
  blue: "4488ff",
  red: "ff4455",
  gray: "888888",
  black: "000000",
};
// Distribution-bar colours for PRINT. The screen draws lavender on a near-black ground;
// on white paper that same pair is unreadable, so the fill keeps the lavender hue and the
// remainder becomes a pale tint of it — the on-screen relationship (one hue, two weights)
// carried onto paper rather than the literal RGB.
export const BAR_FILL = "9966dd";
export const BAR_TRACK = "ece4f6";

// Lighten a "rrggbb" colour toward white by `amount` (0..1) — the print counterpart of the
// screen's `tintHex`, which `strengthColor` takes injected. White, not the page ground,
// because paper is white.
export function tintHexPrint(hex, amount) {
  const h = String(hex).replace(/^#/, "");
  return [0, 2, 4]
    .map((i) => {
      const v = parseInt(h.slice(i, i + 2), 16) || 0;
      return Math.round(v + (255 - v) * amount)
        .toString(16)
        .padStart(2, "0");
    })
    .join("");
}

/**
 * Strip markdown emphasis/heading markers from a string.
 *
 * Two callers, both wanting plain text out of model-authored prose:
 *   • `buildNarrative` — the legacy forecast explanation is rendered as plain text
 *     (see the note there on why rich text was reverted), so markers come off up front
 *     and every consumer downstream, screen and exports alike, gets clean strings.
 *   • `<RichText>`'s Suspense fallback — shows stripped text for the moment before the
 *     markdown chunk loads, so a marker can never flash.
 *
 * @param {string|null|undefined} s — nullish tolerated (callers pass narrative
 *   entries that may be absent); it coerces to "" rather than throwing.
 * @returns {string} the string with `#` heading markers and `*` emphasis removed
 */
export function stripMarkdownMarkers(s) {
  return (
    String(s ?? "")
      // ATX headings only at the START of a line, and only with the space CommonMark
      // requires. `#{1,6}\s*` anywhere was the old rule and it corrupted ordinary
      // prose: it ate the `#` AND the following space, so "Written in C# and F#."
      // became "Written in Cand F." and a URL fragment lost its separator.
      // "#hashtag" / "#1 priority" are not headings and are left alone.
      .replace(/^#{1,6}[ \t]+/gm, "")
      // Emphasis is UNWRAPPED, not deleted: strip the markers only where they
      // actually wrap something. Requiring a non-space right after the opener is
      // CommonMark's own rule and it keeps arithmetic like "2 * 3 percent" intact,
      // which a bare `\*+` erased. A stray unpaired `*` now survives as the literal
      // character it probably was.
      .replace(/\*\*(\S[^*]*?)\*\*/g, "$1")
      .replace(/\*(\S[^*\n]*?)\*/g, "$1")
  );
}

// True when `line` opens a list item — a numbered marker ("1." / "2)") or a
// bullet ("-"/"•") followed by whitespace. Used by buildNarrative's paragraph
// splitter to decide whether a line starts a new paragraph or continues one.
function isListItem(line) {
  return /^\d+[.)]\s/.test(line) || /^[-•]\s/.test(line);
}

// Build the analysis narrative from a forecast source's serverExplanation.
// Returns an array of paragraph strings. `reportSource` is the resolved
// forecast snapshot (may be a history entry or the live state).
export function buildNarrative(reportSource) {
  const rawExpl = reportSource.serverExplanation || reportSource.serverContext?.explanation || "";
  // Some forecasters send a structured object here rather than prose: no narrative then.
  const serverExpl = typeof rawExpl === "string" ? rawExpl : "";
  if (serverExpl.trim()) {
    const cleaned = stripMarkdownMarkers(serverExpl)
      // Markers are stripped up front, before any of the segmenting below.
      //
      // They were briefly preserved so the on-screen render could show the model's
      // bold labels and numbered points as real emphasis. That produced wrong line
      // breaks and was reverted: this function chops the explanation into one
      // paragraph string per line/item, so a markdown parser downstream sees
      // FRAGMENTS rather than blocks and re-flows them. Rendering rich text here
      // requires changing how (or whether) the text is segmented before parsing —
      // a bigger job than the render. Resolution `reasoning` still renders as rich
      // text; it arrives whole, so it doesn't have this problem.
      //
      // Everything else this function does is CONTENT REPAIR for structure-less
      // engines, not markdown handling, and is untouched: the metadata prefix, the
      // numbered-item re-split, continuation-line rejoining, and the semicolon-wall
      // split below.
      // Drop the machine metadata prefix some (structure-less) engines emit —
      // "Probability: 3% Aggregation:" — so the fallback opens on the actual
      // analysis. The probability is already shown prominently up top, and the
      // "Aggregation:" label is just noise before the reasoning.
      .replace(/^\s*probability:\s*\d+(?:\.\d+)?\s*%\s*/i, "")
      .replace(/^\s*aggregation:\s*/i, "");
    // Split on double newlines, then also split numbered items (1. 2. 3.) within each paragraph
    const paras = [];
    for (const block of cleaned.split(/\n\n+/)) {
      const lines = block
        .split(/\n/)
        .map((l) => l.trim())
        .filter(Boolean);
      for (const line of lines) {
        // If line starts with a number+dot (like "1." "2.") treat as separate paragraph
        if (isListItem(line)) {
          paras.push(line);
        } else {
          // Append to last para if it's continuation text, otherwise new para
          if (paras.length && !isListItem(paras[paras.length - 1]) && !line.match(/^[A-Z].*:$/)) {
            paras[paras.length - 1] += " " + line;
          } else {
            paras.push(line);
          }
        }
      }
    }
    // Break a semicolon-joined reasoning run into separate paragraphs so a
    // structure-less explanation reads as distinct points instead of one wall
    // (e.g. "…extremely unlikely.; While …; Even …"). List items and short
    // "Header:" lines are left intact; a paragraph with no "; " is unchanged.
    const out = [];
    for (const p of paras) {
      if (isListItem(p) || /^[A-Z][^.!?]*:$/.test(p) || !/;\s/.test(p)) {
        out.push(p);
      } else {
        for (const piece of p.split(/;\s+/)) {
          const t = piece.trim();
          if (t) out.push(t);
        }
      }
    }
    const filtered = out.filter(Boolean);
    // If the explanation was ONLY the stripped metadata prefix ("Probability: N%
    // Aggregation:") with nothing real after it, fall back to the standard message
    // rather than returning an empty narrative (which would render blank).
    return filtered.length ? filtered : ["No analysis available from the server."];
  }
  return ["No analysis available from the server."];
}

// Shared content spec for the DOCX + PDF exports. Each block is a typed
// descriptor that both renderers iterate; this keeps the two exports in
// lockstep so the PDF is always a rendering of the same content as the DOCX.
// Section headers use black per spec; URLs are tagged with `link` so the
// DOCX renderer can wire them through ExternalHyperlink.
//
// Params:
//   reportSource — the resolved forecast snapshot
//   contract     — reportSource.contract (c in the IIFE)
//   r            — the structured report (reportSource.report || serverContext.report)
//   narrative    — string[] from buildNarrative(reportSource)
//   citationLabels — Map from buildCitationLabels
//   renderedSources — string[] of source URLs/labels
//   keyEvts      — sorted/sliced events array
//   prob         — numeric probability (0..1 or 0..100)
//   branches     — branch run array from selectBranchRuns
export function buildReportBlocks({
  reportSource,
  contract,
  r,
  narrative,
  citationLabels,
  renderedSources,
  keyEvts,
  prob,
  branches,
  // ── Multi-value ("multiway") inputs ────────────────────────────────────────
  // Absent for a binary forecast, in which case every branch below falls back to the
  // binary shape this function has always produced.
  //
  // These exist because the exports were built when every forecast was binary: the whole
  // spec hung off a single `prob`, so a distribution forecast printed `0%` (it has no
  // scalar probability to pass) and none of its content — assessment, per-outcome notes,
  // key considerations, directional drivers — had any way in.
  dist = null, // resolved distribution: {buckets:[{label,p}], kind}
  mr = null, // multi_report: {forecast_summary, per_outcome, key_considerations, sources}
  dr = null, // distribution_reasoning: {drivers_higher, drivers_lower, …}
  assessmentSummary = null, // what the on-screen ASSESSMENT block shows
  // {higher, lower} — the driver columns the SCREEN renders, already normalised. Preferred
  // over re-deriving from `dr`: a driver is `{text, strength}`, and reading `.claim` off it
  // (as the binary reasons carry) yielded blank items — the exports printed bare "H1 / H2 /
  // H3" badges with no content. `dr` stays as the fallback for a caller that passes neither.
  driverItems = null,
  // The two evidence columns the SCREEN renders (`selectEvidenceColumns`): headings, badge
  // letters and items. Derived here from `r`/`dr` when the caller passes none.
  evidence = null,
  // "Quick" / "Deep" — which tier produced this forecast; printed as the last line, with
  // `forecasterName` (debug UI only — the caller passes null otherwise) in parentheses.
  forecastMethod = null,
  forecasterName = null,
}) {
  const c = contract;
  const out = [];
  out.push({ kind: "title", text: "Sayer report", size: 22, color: RC.purple });
  if (reportSource._branchMod)
    out.push({
      kind: "p",
      runs: [{ text: `Branch: ${reportSource._branchMod}`, size: 10, color: RC.black }],
    });
  if (reportSource._engineLabel)
    out.push({
      kind: "p",
      runs: [
        {
          text: `Pipeline: ${reportSource._engineLabel.toUpperCase()}`,
          size: 10,
          color: RC.purple,
        },
      ],
    });
  if (reportSource._partial)
    out.push({
      kind: "p",
      runs: [
        {
          text: "This forecast finished without triggers. Update it to try again.",
          size: 10,
          color: RC.black,
        },
      ],
    });
  out.push({
    kind: "p",
    runs: [
      { text: c.question || c.title || "Untitled Forecast", bold: true, size: 14, color: RC.black },
    ],
    spaceAfter: 10,
  });
  // A DISTRIBUTION forecast has no single probability, so it headlines its leading outcome
  // and then prints the whole distribution as bars. A binary one keeps the single big
  // percentage it always had.
  const _buckets = dist && Array.isArray(dist.buckets) ? dist.buckets.filter(Boolean) : [];
  const isDist = _buckets.length > 0;
  // Does the distribution carry structured content of its own? This decides whether the raw
  // explanation narrative is printed as "Analysis", mirroring the SCREEN's fallback chain:
  // the modal renders driver columns → per-outcome notes → key considerations, and only falls
  // back to the narrative when none of those exist.
  //
  // The export had no such rule. "Analysis" hangs off the `else` of `if (r)`, and a multiway
  // forecast HAS no report object — so it always fell through and dumped the entire
  // explanation, which is where every section's content had gone missing to. The document
  // showed empty structured headings followed by one wall of prose containing all of it.
  if (isDist) {
    // The same leader the screen highlights: the resolved row once settled, else
    // `leadingRow` (which decides when the catch-all leads).
    const _lead = selectLead(_buckets, dist.resolvedIndex).lead;
    if (_lead)
      out.push({
        kind: "p",
        // PERCENT then LABEL — the same order the bar rows below use, the on-screen report
        // uses, and the hover readout uses. It was label-then-percent, which made the headline
        // the one place in the document reading the other way round.
        runs: [
          { text: `${bucketPctText(_lead.p)} `, bold: true, size: 16, color: RC.purple },
          { text: bucketLabel(_lead), bold: true, size: 16, color: RC.black },
        ],
      });
    out.push({
      kind: "bars",
      rows: _buckets.map((b) => ({
        label: bucketLabel(b),
        // Unrounded: a 0.3% outcome must still draw a fill; the text is `pctText`.
        pct: (b.p || 0) * 100,
        pctText: bucketPctText(b.p),
        lead: b === _lead,
      })),
    });
  } else {
    // Normalize the probability to a 0..1 fraction ONCE (prob may arrive as a
    // fraction or a 0..100 percent), then derive everything from it — the color
    // and the larger side's chance — so the heuristic can't drift between them.
    const probFrac = prob > 1 ? prob / 100 : prob;
    const _vc = probFrac >= 0.5 ? RC.blue : RC.red;
    out.push({
      kind: "p",
      runs: [
        {
          text: sideChanceText(probFrac),
          bold: true,
          size: 16,
          color: _vc,
        },
      ],
    });
  }
  out.push({
    kind: "p",
    runs: [
      { text: "FORECAST DATE: ", bold: true, size: 9, color: RC.black },
      { text: fmtMDYTime(reportSource.createdAt), size: 9, color: RC.black },
      { text: "    RESOLUTION DATE: ", bold: true, size: 9, color: RC.black },
      { text: fmtMDY(c.resolution_date), size: 9, color: RC.black },
    ],
    spaceAfter: 12,
  });
  // The same text the on-screen ASSESSMENT block renders. `assessmentSummary` is passed by
  // the caller (which resolves binary's report.forecast_summary vs a distribution's
  // multi_report.forecast_summary through one shared helper); falling back to
  // r.forecast_summary keeps a caller that passes neither working as before.
  const _distStructured =
    isDist &&
    !!(
      driverItems?.higher?.length ||
      driverItems?.lower?.length ||
      dr?.drivers_higher?.length ||
      dr?.drivers_lower?.length ||
      dr?.drivers?.length ||
      dr?.drivers_against?.length ||
      mr?.per_outcome?.length ||
      mr?.key_considerations?.length
    );
  const _summary = assessmentSummary || (r && r.forecast_summary) || null;
  if (_summary) {
    // Heading kept as "FORECAST SUMMARY" deliberately. The on-screen block is labelled
    // ASSESSMENT, so renaming would improve screen/export parity — but it would also change
    // the BINARY export, which is the one that already reads well, and this change is meant
    // to leave that untouched. Rename both together if parity is wanted.
    out.push({ kind: "h2", text: "FORECAST SUMMARY", size: 11, color: RC.black });
    out.push({
      kind: "p",
      runs: [{ text: cleanForecastSummary(_summary), size: 11, color: RC.black }],
    });
  }
  // Hoisted OUT of the `if (r)` below so the distribution path can use them too. A multiway
  // forecast has no `report` object at all, so anything defined inside that guard is
  // unreachable for it — which is why its drivers, notes and considerations printed nowhere.
  const _annot = (arr, prefix) =>
    arr.map((rs, i) => ({
      ...rs,
      prefix,
      itemNum: i + 1,
      _chips: citationChips(rs.citations, citationLabels),
    }));
  // The claim text + its citation chips. The badge and strength pill are NOT runs — the
  // `columns` block carries them as fields so each renderer can lay them out beside the
  // text, the way the screen does.
  const claimRuns = (rs) => {
    const runs = [{ text: rs.claim || "", size: 10, color: RC.black }];
    if (rs._chips.length > 0) {
      runs.push({ text: " [", size: 10, color: RC.gray });
      rs._chips.forEach((c, ci) => {
        if (ci > 0) runs.push({ text: ", ", size: 10, color: RC.gray });
        // Footnote label (e.g. "REUTERS¹") — consistent with the in-app report, and grey
        // like it, not the accent.
        runs.push({ text: c.label, size: 10, color: RC.gray, link: c.source_url || undefined });
      });
      runs.push({ text: "]", size: 10, color: RC.gray });
    }
    return runs;
  };
  // THE TWO EVIDENCE COLUMNS, side by side as on screen. The caller passes the columns the
  // screen renders (`evidence`, from lib/report/evidenceColumns) so headings, badge letters
  // and items match exactly: Yes/No on a binary question, Leader/Field on a categorical one,
  // Higher/Lower on a numeric one. A caller that passes none gets the same derivation here.
  const _evidence =
    evidence ??
    selectEvidenceColumns({
      r,
      dr:
        driverItems && (driverItems.higher?.length || driverItems.lower?.length)
          ? { drivers_higher: driverItems.higher, drivers_lower: driverItems.lower }
          : dr,
      kind: isDist ? dist.kind || "categorical" : null,
      buckets: isDist ? dist.buckets : null,
    });
  const _colItems = (col) =>
    _annot(
      (col?.items || []).filter((d) => d.claim),
      col?.prefix || "",
    );
  const _left = _colItems(_evidence?.left);
  const _right = _colItems(_evidence?.right);
  if (_left.length > 0 || _right.length > 0) {
    const col = (spec, items, color) => ({
      title: spec.label,
      color,
      // The box outline: a pale tint of the column colour, as the screen's `${col}33`.
      boxColor: tintHexPrint(color, 0.7),
      items: items.map((rs) => ({
        badge: `${rs.prefix}${rs.itemNum}`,
        strength: rs.strength ? String(rs.strength).toUpperCase() : "",
        // Lavender weighted by strength, as on screen — the column colour is the header's.
        badgeColor: strengthColor(rs.strength, RC.purple, tintHexPrint),
        runs: claimRuns(rs),
      })),
    });
    out.push({
      kind: "columns",
      columns: [col(_evidence.left, _left, RC.blue), col(_evidence.right, _right, RC.red)],
    });
  }
  if (_left.length > 0 || _right.length > 0) {
    // One appendix entry per distinct source (quotes merged across
    // reasons), keyed by the same report-wide footnote as the inline labels.
    const _allCits = citationChips(
      [..._left, ..._right].flatMap((rs) => rs.citations || []),
      citationLabels,
    );
    if (_allCits.length > 0) {
      out.push({ kind: "h2", text: "Citations", size: 10, color: RC.black });
      _allCits.forEach((e) => {
        const runs = [{ text: `[${e.label}] `, bold: true, size: 10, color: RC.black }];
        if (e.source_url)
          runs.push({ text: e.source_url, link: e.source_url, color: RC.purple, size: 10 });
        if (e.quotes.length)
          runs.push({
            text: ` — ${e.quotes.map((q) => `"${q}"`).join("; ")}`,
            color: RC.gray,
            size: 10,
          });
        out.push({ kind: "p", runs, indent: 24 });
      });
    }
  }
  if (!r && narrative.length > 0 && !_distStructured) {
    out.push({ kind: "h2", text: "Analysis", size: 11, color: RC.black });
    narrative.forEach((p) =>
      out.push({
        kind: "p",
        runs: [
          {
            // No strip needed here: `narrative` comes from buildNarrative (both export
            // call sites share one reportBlockArgs), and that strips markers up front —
            // so these paragraphs are already plain. Stripping again would be dead code
            // implying the markers still arrive. A caller that ever passes RAW
            // explanation text instead would need to strip it itself.
            text: typeof p === "string" ? p : p.text || p.heading || "",
            size: 11,
            color: RC.black,
          },
        ],
      }),
    );
  }
  if (keyEvts.length > 0) {
    out.push({ kind: "h2", text: "KEY FACTORS", size: 11, color: RC.black });
    keyEvts.forEach((ev) => {
      const pEvt = ev.p_event != null ? ev.p_event : ev.probability;
      const arrow = ev.direction === "yes" ? "▲ " : "▼ ";
      const dirCol = ev.direction === "yes" ? RC.blue : RC.red;
      const runs = [
        { text: arrow, bold: true, size: 11, color: dirCol },
        { text: ev.title || "", bold: true, size: 11, color: RC.black },
      ];
      if (pEvt != null)
        runs.push({ text: ` (${Math.round(pEvt * 100)}%)`, size: 11, color: RC.gray });
      if (ev.description)
        runs.push({ text: ` — ${ev.description.split(".")[0]}`, size: 9, color: RC.gray });
      out.push({ kind: "p", runs, indent: 24 });
    });
  }
  if (branches.length > 0) {
    out.push({ kind: "h2", text: "BRANCHES", size: 11, color: RC.black });
    branches.forEach((b) => {
      const bp = b.contract?.probability ?? null;
      out.push({
        kind: "p",
        runs: [
          {
            text: `${bp != null ? sideChanceText(bp) : "..."} — `,
            bold: true,
            size: 11,
            color: RC.black,
          },
          { text: b.modification || "", size: 11, color: RC.black },
        ],
        indent: 24,
      });
    });
  }
  // PER-OUTCOME NOTES and KEY CONSIDERATIONS are distribution-only and were absent from the
  // exports entirely — a large part of "missing most of the content", since for a multi
  // forecast these carry the reasoning the Yes/No evidence lists carry for a binary one.
  if (isDist && Array.isArray(mr?.per_outcome) && mr.per_outcome.length > 0) {
    out.push({ kind: "h2", text: "OUTCOME NOTES", size: 11, color: RC.black });
    mr.per_outcome.forEach((po) => {
      const label = formatBucketLabel(po?.label ?? po?.outcome ?? "");
      const note = po?.note ?? po?.text ?? po?.rationale ?? "";
      if (!label && !note) return;
      out.push({
        kind: "p",
        runs: [
          ...(label ? [{ text: `${label}: `, bold: true, size: 11, color: RC.black }] : []),
          { text: String(note), size: 11, color: RC.black },
        ],
      });
    });
  }
  if (isDist && Array.isArray(mr?.key_considerations) && mr.key_considerations.length > 0) {
    out.push({ kind: "h2", text: "KEY CONSIDERATIONS", size: 11, color: RC.black });
    mr.key_considerations.forEach((k) => {
      const text = typeof k === "string" ? k : (k?.text ?? k?.claim ?? "");
      if (!text) return;
      out.push({
        kind: "p",
        runs: [
          { text: "• ", bold: true, size: 11, color: RC.purple },
          { text: String(text), size: 11, color: RC.black },
        ],
      });
    });
  }
  if ((r && r.resolution_criteria_analysis) || c.resolution_criteria) {
    out.push({ kind: "h2", text: "RESOLUTION CRITERIA", size: 11, color: RC.black });
    if (r && r.resolution_criteria_analysis) {
      (r.resolution_criteria_analysis.criteria || []).forEach((crit, i) => {
        out.push({
          kind: "p",
          runs: [{ text: `${i + 1}. ${crit.statement || ""}`, size: 11, color: RC.black }],
          indent: 24,
        });
        dedupeCitationsByQuote(crit.citations).forEach((ct) => {
          const runs = [{ text: `"${ct.quote || ""}"`, size: 10, color: RC.gray }];
          if (ct.source_url)
            runs.push({
              text: ` — ${ct.source_url}`,
              link: ct.source_url,
              color: RC.purple,
              size: 10,
            });
          out.push({ kind: "p", runs, indent: 48 });
        });
      });
      if (r.resolution_criteria_analysis.summary)
        out.push({
          kind: "p",
          runs: [{ text: r.resolution_criteria_analysis.summary, size: 11, color: RC.black }],
        });
    } else {
      out.push({ kind: "p", runs: [{ text: c.resolution_criteria, size: 11, color: RC.black }] });
    }
  }
  if (r && (r.aggregation || []).length > 0) {
    out.push({ kind: "h2", text: "AGGREGATED CONSIDERATIONS", size: 11, color: RC.black });
    r.aggregation.forEach((p) =>
      out.push({ kind: "p", runs: [{ text: "• " + p, size: 11, color: RC.black }], indent: 24 }),
    );
  }
  if (r && r.calibration_analysis) {
    out.push({ kind: "h2", text: "CALIBRATION ANALYSIS", size: 11, color: RC.black });
    out.push({ kind: "p", runs: [{ text: r.calibration_analysis, size: 11, color: RC.black }] });
  }
  const _sources = renderedSources;
  if (_sources.length > 0) {
    out.push({ kind: "h2", text: "SOURCES", size: 11, color: RC.black });
    _sources.forEach((s) => {
      const isUrl = /^https?:\/\//.test(s);
      const runs = [{ text: "· ", size: 10, color: RC.black }];
      if (isUrl)
        runs.push({
          text: citationLabels.get(s) || srcLabel(s),
          link: s,
          color: RC.purple,
          size: 10,
        });
      else runs.push({ text: s, size: 10, color: RC.black });
      out.push({ kind: "p", runs, indent: 24 });
    });
  }
  if (forecastMethod) {
    out.push({
      kind: "p",
      runs: [
        { text: "FORECAST METHOD: ", bold: true, size: 9, color: RC.gray },
        { text: String(forecastMethod).toUpperCase(), size: 9, color: RC.gray },
        ...(forecasterName ? [{ text: ` (${forecasterName})`, size: 9, color: RC.gray }] : []),
      ],
      spaceAfter: 0,
    });
  }
  return out;
}
